import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PaymentActionError,
  PaymentActionResult,
  PaymentMethodInput,
} from '@cashier/cashier-model';

export interface ConfirmationFormData {
  transactionId: string;
  paymentAttributes: PaymentMethodInput[];
}

export interface ConfirmationDataItem {
  name: string;
  value?: string;
  isValid: boolean;
}

export interface PaymentConfirmationState {
  confirmationData: { [name: string]: ConfirmationDataItem };
  fieldConfigurations: PaymentMethodInput[];
  isLoading: boolean;
}

export const PAYMENT_CONFIRMATION_FEATURE_KEY = 'paymentConfirmation';

export const initialPaymentConfirmationState: PaymentConfirmationState = {
  confirmationData: {},
  fieldConfigurations: [],
  isLoading: false,
};

export const paymentConfirmationSlice = createSlice({
  name: PAYMENT_CONFIRMATION_FEATURE_KEY,
  initialState: initialPaymentConfirmationState,
  reducers: {
    setConfirmationFormData: (
      state,
      action: PayloadAction<ConfirmationFormData>,
    ) => {
      state.fieldConfigurations = action.payload.paymentAttributes;

      const newConfirmationData = {};
      action.payload.paymentAttributes.forEach((attribute) => {
        const { name, value } = attribute;

        newConfirmationData[name] = { name, value, isValid: Boolean(value) };
      });

      state.confirmationData = newConfirmationData;
    },
    changeConfirmationFieldValue: (
      state,
      action: PayloadAction<{ name: string; value: string; isValid: boolean }>,
    ) => {
      const { name, value, isValid } = action.payload;
      const changedDataItem = {
        ...state.confirmationData[name],
        value,
        isValid,
      };

      state.confirmationData = {
        ...state.confirmationData,
        [name]: changedDataItem,
      };
    },
    resetConfirmationFormData: (state) => {
      state.fieldConfigurations =
        initialPaymentConfirmationState.fieldConfigurations;
      state.confirmationData = initialPaymentConfirmationState.confirmationData;
    },
    initiateConfirmation: (state) => {
      state.isLoading = true;
    },
    confirmPaymentStart: (state, { payload }: PayloadAction<any>) => {
      state.isLoading = true;
    },
    confirmPaymentSuccess: (
      state,
      action: PayloadAction<PaymentActionResult>,
    ) => {
      state.isLoading = false;
    },
    confirmPaymentFailure: (
      state,
      action: PayloadAction<PaymentActionError>,
    ) => {
      state.isLoading = false;
    },
  },
});

export const paymentConfirmationReducer = paymentConfirmationSlice.reducer;

export const {
  setConfirmationFormData,
  resetConfirmationFormData,
  initiateConfirmation,
  confirmPaymentStart,
  confirmPaymentSuccess,
  confirmPaymentFailure,
  changeConfirmationFieldValue,
} = paymentConfirmationSlice.actions;
